import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ImageUploader from "../components/ImageUploader";
import SkeletonLoader from "../components/SkeletonLoader";
import BillingSummary from "../components/BillingSummary";
import {
  generateModifiedImage,
  saveGeneratedImage,
} from "../api/imageGenerationAPI";

// SimliE2E SDK constants - default values
const DEFAULT_SIMLI_FACE_ID = "137aa749-6bcd-44d4-989f-a6c666fdf871";
const DEFAULT_SIMLI_VOICE_ID = "8bc1c0f2-3013-4b96-8acc-8182fa71fa86";

function YooCreatePage() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [customizationText, setCustomizationText] = useState("");
  const [modifiedImageUrl, setModifiedImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [customizationHistory, setCustomizationHistory] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // SimliE2E related states
  const [faceId, setFaceId] = useState(() => {
    return localStorage.getItem("faceId") || DEFAULT_SIMLI_FACE_ID;
  });
  const [voiceId, setVoiceId] = useState(() => {
    return localStorage.getItem("voiceId") || DEFAULT_SIMLI_VOICE_ID;
  });
  const [simliReady, setSimliReady] = useState(false);
  const [isSimliPlaying, setIsSimliPlaying] = useState(false);
  const [isSimliLoading, setIsSimliLoading] = useState(false);
  const [roomUrl, setRoomUrl] = useState(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const simliContainerRef = useRef(null);
  const simliInstance = useRef(null);

  // Store faceId and voiceId in localStorage if they change
  useEffect(() => {
    if (faceId) {
      localStorage.setItem("faceId", faceId);
    }
    if (voiceId) {
      localStorage.setItem("voiceId", voiceId);
    }
  }, [faceId, voiceId]);

  // Re-initialize SimliE2E when faceId or voiceId changes
  useEffect(() => {
    if (simliInstance.current) {
      simliInstance.current.destroy();
      setTimeout(() => {
        initializeSimli();
      }, 100);
    }
  }, [faceId, voiceId]);

  // Initialize SimliE2E
  useEffect(() => {
    // Check if SimliE2E SDK is available
    if (window.SimliE2E) {
      initializeSimli();
    } else {
      // Load SimliE2E SDK script if not available
      const script = document.createElement("script");
      script.src = "https://sdk.simli.ai/latest/simli-e2e.min.js";
      script.async = true;
      script.onload = initializeSimli;
      document.body.appendChild(script);
    }

    return () => {
      // Cleanup SimliE2E instance on component unmount
      if (simliInstance.current) {
        simliInstance.current.destroy();
      }
    };
  }, []);

  // Auto-start the avatar session
  useEffect(() => {
    // Wait a short delay after page load to start the session
    const timer = setTimeout(() => {
      if (!roomUrl && !isStartingSession) {
        startAvatarSession();
      }
    }, 1500); // 1.5 second delay for better user experience

    return () => clearTimeout(timer);
  }, []);

  // Initialize SimliE2E instance
  const initializeSimli = async () => {
    try {
      if (simliContainerRef.current && window.SimliE2E) {
        setIsSimliLoading(true);

        // Create SimliE2E instance
        simliInstance.current = new window.SimliE2E({
          container: simliContainerRef.current,
          faceId: faceId,
          voiceId: voiceId,
          initialMood: "happy",
          autoplay: false,
        });

        // Initialize instance
        await simliInstance.current.initialize();
        setSimliReady(true);
        setIsSimliLoading(false);

        // No need to play welcome message here anymore since we'll start a full session
      }
    } catch (err) {
      console.error("Failed to initialize SimliE2E:", err);
      setError("Failed to load avatar. Please try again later.");
      setIsSimliLoading(false);
    }
  };

  // Start avatar session
  const startAvatarSession = async () => {
    if (!faceId || !voiceId) {
      setError("Face ID and Voice ID are required");
      return;
    }

    try {
      setIsStartingSession(true);

      // Get user's first name from localStorage or use a default
      const firstName = localStorage.getItem("firstName") || "User";

      // Prepare system prompt for customization help
      const systemPrompt =
        "You are a helpful AI assistant specialized in image customization. Help the user understand how to use the image customization features. Be encouraging and friendly.";

      // Welcome message for YooCreate
      const welcomeMessage = `Hi ${firstName}, in Yoo Create we can use AI to generate whatever changes you might like to make to your appearance. For example, you can say, 'Change eye color to green' to change your eyes. Or 'Add a crown to my head' to add a crown to your avatar. Please let me know if you would like to customize your appearance. And if so, how?`;

      const payload = {
        apiKey: "n8sesdejo1tnudor5ljcg",
        faceId: faceId,
        voiceId: voiceId,
        ttsProvider: "Cartesia",
        ttsModel: "sonic-turbo-2025-03-07",
        ttsAPIKey: "sk_car_ANhQon9hRbqMDBgR5Z5n9Q",
        systemPrompt: systemPrompt,
        firstMessage: welcomeMessage,
        language: "en",
        maxSessionLength: 3600,
        maxIdleTime: 300,
      };

      const res = await fetch("https://api.simli.ai/startE2ESession", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Session start failed: ${res.status} - ${errText}`);
      }

      const result = await res.json();

      if (result?.roomUrl) {
        setRoomUrl(result.roomUrl);
      } else {
        throw new Error("No room URL returned from session creation");
      }
    } catch (err) {
      setError("Failed to start avatar session: " + err.message);
      console.error("Session error:", err);
    } finally {
      setIsStartingSession(false);
    }
  };

  // Clear error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle image upload
  const handleImageSelected = (file) => {
    setUploadedImage(file);
    // Reset the modified image when a new image is uploaded
    setModifiedImageUrl(null);
    setCustomizationHistory([]);
  };

  // Generate the modified image
  const generateImage = async () => {
    if (!uploadedImage) {
      setError("Please upload an image first");
      return;
    }

    if (customizationText.trim() === "") {
      setError("Please add a customization");
      return;
    }

    setIsGenerating(true);

    try {
      // Call the API utility to generate the modified image
      const imageUrl = await generateModifiedImage(
        uploadedImage,
        customizationText
      );

      // Add the customization to history
      setCustomizationHistory([...customizationHistory, customizationText]);

      // Set the modified image and clear the input
      setModifiedImageUrl(imageUrl);
      setCustomizationText("");
    } catch (err) {
      console.error("Error generating image:", err);
      setError(`Failed to generate image: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Export the image with improved error handling
  const exportImage = async () => {
    if (!modifiedImageUrl) {
      setError("No modified image to export");
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      // Extract filename from URL or use a default
      const filename =
        modifiedImageUrl.split("/").pop() ||
        `ai-generated-image-${Date.now()}.png`;

      // Add timeout to fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      // Fetch the image data with timeout
      const response = await fetch(modifiedImageUrl, {
        signal: controller.signal,
        credentials: "omit", // Don't send cookies for cross-origin requests
        mode: "cors", // Explicitly request CORS
      });

      // Clear timeout
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch image: ${response.status} ${response.statusText}`
        );
      }

      // Convert to blob
      const blob = await response.blob();

      // Verify we received valid image data
      if (!blob.type.startsWith("image/")) {
        throw new Error("The server did not return a valid image");
      }

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create download link
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      console.log("Image downloaded successfully:", filename);
    } catch (err) {
      console.error("Error exporting image:", err);
      // Provide more user-friendly error messages
      if (err.name === "AbortError") {
        setError(
          "Download timed out. Please try again or check your network connection."
        );
      } else if (err.message.includes("CORS")) {
        setError(
          "Cross-origin issue detected. The server might need CORS configuration."
        );
      } else {
        setError(`Failed to download image: ${err.message}`);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header with top-right avatar */}
          <div className="relative mb-8">
            {/* Header text */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900">Yoo Create</h1>
              <p className="mt-2 text-lg text-gray-600">
                Upload an image, customize it with AI, and export your creation
              </p>
            </div>

            {/* Avatar container positioned at top right */}
            <div className="fixed top-0 right-0 w-[300px] h-[250px]">
              {roomUrl ? (
                <div className="w-full h-full flex flex-col rounded-lg overflow-hidden shadow-md">
                  <iframe
                    src={roomUrl}
                    title="Simli Avatar Session"
                    className="w-full flex-grow"
                    allow="camera; microphone; fullscreen"
                  />
                  <button
                    onClick={() => setRoomUrl(null)}
                    className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 w-full"
                  >
                    Close Session
                  </button>
                </div>
              ) : (
                <div
                  ref={simliContainerRef}
                  className="w-full h-full flex items-center justify-center relative bg-gray-50 rounded-lg shadow-md border border-gray-200"
                >
                  {isSimliLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-70 z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  )}

                  {isStartingSession && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 bg-opacity-70 z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-3"></div>
                      <p className="text-sm text-gray-700">
                        Starting avatar session...
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left side - Image upload and generation */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Upload Your Image
                  </h2>
                  <ImageUploader
                    onImageSelected={handleImageSelected}
                    className="mb-4"
                  />

                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Customization Options
                    </h2>
                    <div>
                      <label
                        htmlFor="customization"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {modifiedImageUrl
                          ? "Add another customization"
                          : "Describe how you want to modify the image"}
                      </label>
                      <textarea
                        id="customization"
                        rows="3"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#23c55e] focus:border-[#23c55e]"
                        placeholder={
                          modifiedImageUrl
                            ? "E.g., Make the sky more purple"
                            : "E.g., Make the background a beach sunset"
                        }
                        value={customizationText}
                        onChange={(e) => setCustomizationText(e.target.value)}
                      />
                    </div>

                    <button
                      onClick={generateImage}
                      disabled={isGenerating || !uploadedImage}
                      className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                        isGenerating || !uploadedImage
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-[#23c55e] hover:bg-[#1ea04d] transition-colors"
                      }`}
                    >
                      {isGenerating
                        ? "Generating..."
                        : modifiedImageUrl
                        ? "Update Image"
                        : "Generate Image"}
                    </button>
                  </div>

                  {customizationHistory.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-md font-medium text-gray-700">
                        Customization History:
                      </h3>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {customizationHistory.map((customization, index) => (
                          <li key={index} className="bg-gray-50 p-2 rounded">
                            {index + 1}. {customization}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Right side - Image preview and export */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Image Preview
                  </h2>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 h-100 flex justify-center items-center">
                    {isGenerating ? (
                      <div className="text-center w-full">
                        <SkeletonLoader type="image" />
                        <p className="mt-4 text-gray-500">
                          Generating your image...
                        </p>
                      </div>
                    ) : modifiedImageUrl ? (
                      <img
                        src={modifiedImageUrl}
                        alt="Modified"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <p className="text-gray-500">
                        Your modified image will appear here
                      </p>
                    )}
                  </div>

                  {modifiedImageUrl && (
                    <button
                      onClick={exportImage}
                      disabled={isDownloading}
                      className={`w-full py-2 px-4 text-white font-medium rounded-md transition-colors ${
                        isDownloading
                          ? "bg-blue-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    >
                      {isDownloading ? "Downloading..." : "Download Image"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="mt-8 flex justify-between">
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Studio
            </Link>
            <Link
              to="/yoo-chat"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Go to Yoo Chat
            </Link>
          </div>
        </div>
      </div>

      {/* Sticky footer group */}
      <div className="w-full mt-auto">
        {/* Billing Summary */}
        <BillingSummary />

        {/* Footer */}
        <footer className="bg-white py-4 border-t border-gray-200 text-center text-gray-600 text-sm">
          <p>© 2023 YOO! All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default YooCreatePage;
